import {
  Box,
  Button,
  PasswordInput,
  Text,
  TextInput,
  Title
} from "@mantine/core";
import type { FormEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { ApiError } from "../../api";
import { ROUTES } from "../../routes";
import urls from "../../urls";
import { SiteName } from "../../utils/enums";
import { PublicLayout } from "../Layout/PublicLayout";
import "./Login.scss";
import { useSsoConfiguration } from "./useSsoConfiguration";
import { checkAuth } from "./utils/checkAuth";
import { login } from "./utils/login";
import { sanitizeRedirectUrl } from "./utils/sanitizeRedirectUrl";

declare const SITE_NAME: SiteName;

const handleRoedlLogin = (redirectUrl: string) => {
  location.replace(
    `${urls.auth.roedlLogin()}?next=${encodeURIComponent(redirectUrl)}`
  );
};

const handleMicrosoftLogin = (redirectUrl: string) => {
  location.replace(
    `${urls.auth.microsoftLogin()}?next=${encodeURIComponent(redirectUrl)}`
  );
};

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { ssoConfig } = useSsoConfiguration();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState<string>();
  const [loginInProgress, setLoginInProgress] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const redirectUrl = useMemo(
    () =>
      sanitizeRedirectUrl(searchParams.get("next"), ROUTES.productSelectPage),
    [searchParams]
  );

  const handleLogin = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setLoginInProgress(true);

      try {
        const loginInfo = await login(username, password);

        if (loginInfo?.ephemeralToken) {
          navigate(
            `/login/code?method=${loginInfo.method}&email=${username}&next=${encodeURIComponent(redirectUrl)}`
          );
          return;
        }

        location.replace(redirectUrl);
      } catch (e) {
        setLoginError((e as ApiError<{ error: string }>).response?.data.error);
      } finally {
        setLoginInProgress(false);
      }
    },
    [password, redirectUrl, username, navigate]
  );

  useEffect(() => {
    const prepareRender = async () => {
      const hasAccess = await checkAuth(redirectUrl);

      setIsReady(hasAccess);
    };

    prepareRender();
  }, [redirectUrl]);

  return isReady ? (
    <PublicLayout>
      <form className="LoginForm" id="login-form" onSubmit={handleLogin}>
        <div className="LoginIconContainer">
          <div className="LoginIcon"></div>
        </div>

        <Title c="var(--mantine-color-neutral-6)" fz={16} mb="1.5rem" order={3}>
          Anmelden
        </Title>

        <Box>
          <TextInput
            label=""
            name="auth-username"
            placeholder="Email"
            required
            type="text"
            value={username}
            onChange={(event) => setUsername(event.currentTarget.value)}
          />
          <PasswordInput
            label=""
            mt="sm"
            name="auth-password"
            placeholder="Passwort"
            required
            value={password}
            withAsterisk={!password}
            onChange={(event) => setPassword(event.currentTarget.value)}
          />
        </Box>

        {loginError && (
          <Text c="error" my="md">
            Ungültige Zugangsdaten. Bitte erneut versuchen
          </Text>
        )}

        <div>
          <Button
            className="LoginFilledBtn"
            color="primary"
            disabled={!username || !password || loginInProgress}
            loading={loginInProgress}
            type="submit"
          >
            Login
          </Button>
        </div>

        {SITE_NAME === SiteName.Smarendo && ssoConfig?.roedl && (
          <div>
            <Button
              className="LoginOutlinedBtn"
              color="primary"
              variant="outline"
              onClick={() => handleRoedlLogin(redirectUrl)}
            >
              Login für Rödl&Partner-Mitarbeiter
            </Button>
          </div>
        )}

        {ssoConfig?.microsoft && (
          <div>
            <Button
              className="LoginOutlinedBtn"
              color="primary"
              variant="outline"
              onClick={() => handleMicrosoftLogin(redirectUrl)}
            >
              Login mit einem Microsoft-Konto
            </Button>
          </div>
        )}

        <div className="ForgotPassword">
          <Link className="brand-color-link" to={`/password-reset`}>
            Passwort vergessen?
          </Link>
        </div>
      </form>
    </PublicLayout>
  ) : null;
}

export { Login };
